// SDP munges the iMX6 hub VPU requires, ported verbatim from the Flutter client
// (janus_call_session.dart). Applied to the publisher's local offer before
// configure() — see plans/janus-call-migration.md §3. Pure string transforms.

/**
 * Reorder the m=video payload list so H.264 baseline/constrained-baseline
 * payloads (profile-level-id starting with `42`) lead. Returns the SDP
 * unchanged if there's no m=video line or no baseline H.264 payload.
 */
export const preferH264Baseline = (sdp: string): string => {
  const lines = sdp.split(/\r\n|\n/);

  const codecOf: Record<string, string> = {};
  const fmtpOf: Record<string, string> = {};
  for (const line of lines) {
    const r = /^a=rtpmap:(\d+) ([^/]+)\//.exec(line);
    if (r) {
      codecOf[r[1]] = r[2].toLowerCase();
      continue;
    }
    const f = /^a=fmtp:(\d+) (.*)$/.exec(line);
    if (f) fmtpOf[f[1]] = f[2].toLowerCase();
  }

  const isBaselineH264 = (pt: string): boolean => {
    if (codecOf[pt] !== 'h264') return false;
    const m = /profile-level-id=([0-9a-f]{6})/.exec(fmtpOf[pt] ?? '');
    return m != null && m[1].startsWith('42');
  };

  const idx = lines.findIndex((l) => l.startsWith('m=video '));
  if (idx < 0) return sdp;

  // m=video <port> <proto> <pt>...
  const parts = lines[idx].split(' ');
  if (parts.length <= 3) return sdp;
  const pts = parts.slice(3);
  const baseline = pts.filter(isBaselineH264);
  if (baseline.length === 0) return sdp;
  const rest = pts.filter((pt) => !baseline.includes(pt));
  lines[idx] = [...parts.slice(0, 3), ...baseline, ...rest].join(' ');
  return lines.join('\r\n');
};

/**
 * Cap every H.264 profile-level-id to level 3.1 (`…1f`), keeping the
 * profile/constraint bytes (`42e029` → `42e01f`). Only the 6-hex H.264 value
 * matches, so Opus/other fmtp lines are untouched.
 */
export const capH264Level31 = (sdp: string): string =>
  sdp.replace(
    /(profile-level-id=[0-9a-fA-F]{4})[0-9a-fA-F]{2}/g,
    (_m, prefix: string) => `${prefix}1f`,
  );

/**
 * Drop the CVO header-ext (any extmap id) so libwebrtc bakes rotation into the
 * encoded pixels — the hub's GStreamer ignores urn:3gpp:video-orientation.
 */
export const stripVideoOrientation = (sdp: string): string =>
  sdp.replace(/a=extmap:\d+ urn:3gpp:video-orientation\r?\n/g, '');

/** Apply all three offer munges in the same order as the Flutter client. */
export const mungeOfferSdp = (sdp: string): string =>
  stripVideoOrientation(capH264Level31(preferH264Baseline(sdp)));
